import React, { useState, useRef } from 'react';
import { supabase } from '../supabase';
import { getChatRequestUrl } from '../config';
import { prepareImageForUpload } from '../utils/imageCompress';
import { parseSummaryBullets } from './SkinTimeline';

const METRICS = [
  { key: 'breakouts', label: 'Breakouts' },
  { key: 'dryness', label: 'Dryness' },
  { key: 'texture', label: 'Texture' },
  { key: 'redness', label: 'Redness' },
  { key: 'darkSpots', label: 'Dark Spots' },
];

const ANGLES = ['Full face', 'Left cheek', 'Right cheek', 'Chin'];

function Slider({ label, value, onChange, min = 0, max = 10, minLabel, maxLabel }) {
  return (
    <div>
      {label && <p className="font-medium text-[#2A324B] text-sm mb-2">{label}</p>}
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
        style={{
          background: `linear-gradient(to right, #2A324B ${(value / max) * 100}%, #D8D8D8 ${(value / max) * 100}%)`,
        }}
      />
      <div className="flex justify-between text-xs text-[#2A324B]/40 mt-1.5">
        <span>{minLabel || `${min === 0 ? 'None' : 'Poor'} (${min})`}</span>
        <span className="font-semibold text-[#2A324B]">{value}/{max}</span>
        <span>{maxLabel || `${min === 0 ? 'Severe' : 'Excellent'} (${max})`}</span>
      </div>
    </div>
  );
}

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Parse structured JSON block from AI response. AI is asked to return:
//   {"overall": 7, "breakouts": 2, "dryness": 4, "texture": 3, "redness": 1, "darkSpots": 2, "summary": "..."}
// inside a ```json fence. Falls back gracefully if parse fails.
function parseAIStructured(text) {
  if (!text) return null;
  try {
    const fence = text.match(/```json\s*([\s\S]*?)```/i);
    const raw = fence ? fence[1] : text.match(/\{[\s\S]*\}/)?.[0];
    if (!raw) return null;
    const obj = JSON.parse(raw);
    return obj;
  } catch (_) {
    return null;
  }
}

// entry prop is present when editing an existing entry
// `hasImageConsent` + `requestImageConsent` come from ChatComponent — Skin Journal
// uploads face/skin photos, so they must travel through the same consent gate
// as the chat selfie path (otherwise this is a privacy loophole).
export default function AddEntryModal({
  userId,
  onClose,
  onSaved,
  entry,
  hasRoutine = false,
  onUploadRoutine,
  hasImageConsent = false,
  requestImageConsent,
}) {
  const isEditing = !!entry;

  // Gate the file picker on consent. If we don't have consent yet, ask first;
  // open the picker only if the user grants. `mode='camera'` opens the device
  // camera directly (matters on Android — iOS shows the same sheet either way).
  const openPhotoPicker = async (mode = 'library') => {
    if (!hasImageConsent && requestImageConsent) {
      const granted = await requestImageConsent();
      if (!granted) return;
    }
    if (mode === 'camera') {
      photoCameraInputRef.current?.click();
    } else {
      photoFileInputRef.current?.click();
    }
  };

  // Photos state: { path, preview, isExisting } | { file, preview, isExisting: false, uploadedPath? }
  const [photos, setPhotos] = useState(() => {
    if (!entry) return [];
    return (entry.photo_paths || []).map((path, i) => ({
      path,
      preview: entry.photoUrls?.[i] || null,
      isExisting: true,
    }));
  });

  const [overallScore, setOverallScore] = useState(entry?.overall_score ?? 5);
  const [metrics, setMetrics] = useState(
    entry?.skin_metrics
      ? { breakouts: 0, dryness: 0, texture: 0, redness: 0, darkSpots: 0, ...entry.skin_metrics }
      : { breakouts: 0, dryness: 0, texture: 0, redness: 0, darkSpots: 0 }
  );
  const [notes, setNotes] = useState(entry?.notes || '');
  const [aiSummary, setAiSummary] = useState(entry?.ai_summary || null);
  const [aiFilled, setAiFilled] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const photoFileInputRef = useRef();
  // Camera-direct input — `capture="user"` lets Android jump straight into the
  // front camera (iOS shows the same sheet either way). Skin Journal is by
  // definition a selfie flow, so front camera is the right default.
  const photoCameraInputRef = useRef();

  // ── Photo handlers ───────────────────────────────────────────────────────────
  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    const newPhotos = files.map(f => ({ file: f, preview: URL.createObjectURL(f), isExisting: false }));
    setPhotos(prev => [...prev, ...newPhotos].slice(0, 4));
    e.target.value = '';
  };

  const removePhoto = (index) => {
    setPhotos(prev => {
      const photo = prev[index];
      if (!photo.isExisting && photo.preview) URL.revokeObjectURL(photo.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  // Upload a single photo to Supabase if not already uploaded. Returns { path, signedUrl }
  const ensureUploaded = async (photo) => {
    if (photo.isExisting && photo.path) {
      const { data, error: signErr } = await supabase.storage
        .from('images').createSignedUrl(photo.path, 604800);
      if (signErr) throw signErr;
      return { path: photo.path, signedUrl: data.signedUrl };
    }
    if (photo.uploadedPath) {
      const { data, error: signErr } = await supabase.storage
        .from('images').createSignedUrl(photo.uploadedPath, 604800);
      if (signErr) throw signErr;
      return { path: photo.uploadedPath, signedUrl: data.signedUrl };
    }
    const safeName = photo.file.name.replace(/[^a-z0-9.]/gi, '_');
    const filePath = `uploaded_images/${userId}/${Date.now()}_${safeName}`;
    const { error: uploadError } = await supabase.storage
      .from('images').upload(filePath, photo.file, { contentType: photo.file.type, upsert: false });
    if (uploadError) throw uploadError;
    const { data, error: signErr } = await supabase.storage
      .from('images').createSignedUrl(filePath, 604800);
    if (signErr) throw signErr;
    photo.uploadedPath = filePath; // mutate so we don't re-upload on save
    return { path: filePath, signedUrl: data.signedUrl };
  };

  // ── AI Fill ──────────────────────────────────────────────────────────────────
  const handleAIFill = async () => {
    if (photos.length === 0 || scanLoading) return;
    setScanLoading(true);
    setScanError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken || !userId) throw new Error('Not authenticated');

      // Use the first photo for analysis
      const first = photos[0];
      const { signedUrl } = await ensureUploaded(first);

      // Backend gates vision analysis on the presence of image_data (base64).
      // Phone photos blow Lambda's 6 MB sync payload cap unless compressed, so
      // we run them through resize + JPEG 0.8 first (same as the chat upload
      // path). For existing entries we already have a signed URL but no file,
      // so we skip the base64 and rely on the backend's URL-fetch fallback —
      // but those entries are rare in practice (re-running AI on an old entry).
      let imageBase64 = null;
      if (!first.isExisting && first.file) {
        const { base64 } = await prepareImageForUpload(first.file);
        imageBase64 = base64;
      }

      // Fold the user's own note into the prompt so the AI addresses what
      // they flagged (e.g. "new pimple on chin") rather than giving a bare grade.
      const userNote = notes.trim();
      const noteContext = userNote
        ? `The person tracking their skin wrote this note for today: "${userNote}". Address it specifically in your summary. `
        : '';

      const apiUrl = getChatRequestUrl();
      const resp = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
        body: JSON.stringify({
          message:
            noteContext +
            'Analyse my skin health. Return your assessment as a JSON object inside a ```json fence with these fields: ' +
            '{"overall": 1-10, "breakouts": 0-10, "dryness": 0-10, "texture": 0-10, "redness": 0-10, "darkSpots": 0-10, "summary": "3-5 short bullet points that JUSTIFY the overall score — each bullet on its own line starting with \'- \', naming what you see and why it lands at that number, and responding to the user\'s note if they left one. Keep each bullet to one concise sentence."}. ' +
            'Higher overall = better skin. For concerns, higher = more severe. Be honest, specific, and explain your reasoning.',
          userId,
          ...(imageBase64 ? { image_data: imageBase64 } : {}),
          image_url: signedUrl,
          analysis_type: 'skin',
          ephemeral: true, // utility call — keep out of chat history
          accessToken,
        }),
      });

      const data = await resp.json();
      if (!data.success) throw new Error(data.message || 'Analysis failed');

      const text = data.message || '';
      const parsed = parseAIStructured(text);

      // The AI's read goes into aiSummary (shown as the Son:mi assessment /
      // justification). The user's own note stays untouched — they're separate:
      // the note is the user's voice, the summary is the AI's.
      if (parsed) {
        if (typeof parsed.overall === 'number') setOverallScore(Math.max(1, Math.min(10, Math.round(parsed.overall))));
        setMetrics(prev => ({
          breakouts: typeof parsed.breakouts === 'number' ? Math.round(parsed.breakouts) : prev.breakouts,
          dryness: typeof parsed.dryness === 'number' ? Math.round(parsed.dryness) : prev.dryness,
          texture: typeof parsed.texture === 'number' ? Math.round(parsed.texture) : prev.texture,
          redness: typeof parsed.redness === 'number' ? Math.round(parsed.redness) : prev.redness,
          darkSpots: typeof parsed.darkSpots === 'number' ? Math.round(parsed.darkSpots) : prev.darkSpots,
        }));
        setAiSummary((parsed.summary || '').slice(0, 800));
      } else {
        // Fallback: no JSON parsed — keep the prose as the summary
        setAiSummary(text.slice(0, 800));
      }

      setAiFilled(true);
    } catch (err) {
      console.error('AI fill error:', err);
      setScanError('Couldn’t analyse — you can still rate manually.');
    } finally {
      setScanLoading(false);
    }
  };

  // ── Save ─────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      // Upload any photos that haven't been uploaded yet (AI fill may have already done this for the first one)
      const allPaths = [];
      for (const photo of photos) {
        if (photo.isExisting && photo.path) {
          allPaths.push(photo.path);
        } else if (photo.uploadedPath) {
          allPaths.push(photo.uploadedPath);
        } else {
          const { path } = await ensureUploaded(photo);
          allPaths.push(path);
        }
      }

      const source = aiFilled
        ? 'track_scan'
        : (isEditing ? (entry.source || 'manual') : 'manual');

      const payload = {
        overall_score: overallScore,
        skin_metrics: metrics,
        notes: notes.trim() || null,
        photo_paths: allPaths,
        source,
        ai_summary: aiSummary || (isEditing ? entry.ai_summary : null) || null,
      };

      if (isEditing) {
        const { error: updateError } = await supabase
          .from('skin_journal_entries')
          .update(payload)
          .eq('id', entry.id)
          .eq('user_id', userId);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('skin_journal_entries')
          .insert({ user_id: userId, ...payload });
        if (insertError) throw insertError;
      }

      onSaved();
    } catch (err) {
      console.error('Error saving entry:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const hasPhotos = photos.length > 0;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center sm:items-center">
      <div className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl max-h-[92vh] flex flex-col">

        {/* Header */}
        <div className="px-5 pt-5 pb-3 flex items-center gap-3 flex-shrink-0">
          <button onClick={onClose} className="p-1.5 hover:bg-[#F5F5F5] rounded-xl transition-colors">
            <svg className="w-5 h-5 text-[#2A324B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex-1">
            <h2 className="font-bold text-[#2A324B] text-lg leading-tight">
              {isEditing ? 'Edit Entry' : 'Log Progress'}
            </h2>
            <p className="text-xs text-[#2A324B]/50">Track your skin's journey</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F5F5F5] rounded-xl transition-colors">
            <svg className="w-5 h-5 text-[#2A324B]/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 pb-4 space-y-6">

          {/* ── Photos section ─────────────────────────────────────────── */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-[#2A324B] text-sm">Photos</h3>
              <span className="text-xs text-[#2A324B]/40">{photos.length}/4</span>
            </div>

            <div
              className={`border-2 border-dashed border-[#F3D5B5] rounded-2xl ${hasPhotos ? 'p-3' : 'p-6'} transition-colors`}
            >
              {!hasPhotos ? (
                <div className="flex flex-col items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-[#F5F5F5] flex items-center justify-center">
                    <svg className="w-7 h-7 text-[#2A324B]/25" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div className="text-center">
                    <p className="font-semibold text-[#2A324B]">Progress photos</p>
                    <p className="text-sm text-[#2A324B]/45 mt-0.5">Multiple angles give a better read</p>
                  </div>
                  {/* Two CTAs so Android users can actually use their camera.
                      iOS works either way — both inputs accept image/*. */}
                  <div className="w-full flex flex-col gap-2 mt-1 max-w-xs">
                    <button
                      type="button"
                      onClick={() => openPhotoPicker('camera')}
                      className="bg-[#2A324B] text-[#F3D5B5] px-5 py-2.5 rounded-full text-sm font-semibold flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Take photo
                    </button>
                    <button
                      type="button"
                      onClick={() => openPhotoPicker('library')}
                      className="border border-[#2A324B]/20 text-[#2A324B] px-5 py-2 rounded-full text-sm font-medium hover:bg-[#FDF8F4] transition-colors"
                    >
                      Choose from library
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {photos.map((p, i) => (
                    <div key={i} className="relative aspect-square rounded-xl overflow-hidden bg-[#F5F5F5]">
                      {p.preview
                        ? <img src={p.preview} alt="" className="w-full h-full object-cover" />
                        : <div className="w-full h-full bg-[#F0F0F0]" />}
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); removePhoto(i); }}
                        className="absolute top-1.5 right-1.5 bg-black/50 rounded-full p-1"
                      >
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                  {photos.length < 4 && (
                    <button
                      type="button"
                      onClick={() => openPhotoPicker('camera')}
                      className="aspect-square rounded-xl border-2 border-dashed border-[#F3D5B5] flex items-center justify-center bg-[#FDF8F4] hover:bg-[#F8EDE0] transition-colors"
                    >
                      <svg className="w-6 h-6 text-[#2A324B]/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Library picker (no `capture` attr) and camera-direct (`capture`
                set) — Android needs the explicit camera input to offer Take
                Photo, iOS handles both identically. */}
            <input
              ref={photoFileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handlePhotoSelect}
            />
            <input
              ref={photoCameraInputRef}
              type="file"
              accept="image/*"
              capture="user"
              className="hidden"
              onChange={handlePhotoSelect}
            />

            {/* Angle chips — always visible as a guide */}
            {hasPhotos && (
              <div className="flex gap-1.5 flex-wrap">
                <span className="text-xs text-[#2A324B]/45 self-center mr-1">Suggested:</span>
                {ANGLES.map(angle => (
                  <span key={angle} className="text-xs bg-[#FDF6EE] text-[#2A324B]/70 px-2 py-1 rounded-lg border border-[#F3D5B5]/50">
                    {angle}
                  </span>
                ))}
              </div>
            )}

            {/* User note — sits BEFORE the AI button so the user can flag
                what they want looked at (e.g. "new pimple on chin"); the AI
                fill folds this into its prompt. */}
            <div>
              <label className="block text-sm font-semibold text-[#2A324B] mb-1.5">
                What's on your mind? <span className="font-normal text-[#2A324B]/40">(optional)</span>
              </label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Anything you've noticed — a new spot, dryness, a reaction… Son:mi will look at this when you run the analysis."
                rows={3}
                className="w-full bg-[#F5F5F5] rounded-2xl p-3.5 text-sm text-[#2A324B] placeholder-[#2A324B]/35 resize-none outline-none focus:ring-2 focus:ring-[#F3D5B5]/60"
              />
            </div>

            {/* AI fill button */}
            <button
              type="button"
              onClick={handleAIFill}
              disabled={!hasPhotos || scanLoading}
              className={`w-full py-3 rounded-2xl font-semibold text-sm flex items-center justify-center gap-2 transition-colors ${
                !hasPhotos || scanLoading
                  ? 'bg-[#F0F0F0] text-[#2A324B]/30'
                  : 'bg-[#F3D5B5] text-[#2A324B] hover:bg-[#EDC79D]'
              }`}
            >
              {scanLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-[#2A324B]/30 border-t-[#2A324B] rounded-full animate-spin" />
                  Analysing your skin…
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  {aiFilled ? 'Re-run AI analysis' : 'Let AI fill this in'}
                </>
              )}
            </button>

            {scanError && (
              <div className="bg-red-50 rounded-xl p-3 flex items-center gap-2">
                <svg className="w-4 h-4 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-red-600">{scanError}</p>
              </div>
            )}

            {aiSummary && aiFilled && (() => {
              // Match the SkinTimeline feed: parse the AI summary into proper
              // bullets so the modal preview and the saved entry look the same.
              // Falls back to prose only if the parser can't find a bullet shape.
              const bullets = parseSummaryBullets(aiSummary);
              return (
                <div className="bg-[#F8F6F3] rounded-2xl p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-[#F3D5B5] flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 text-[#2A324B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    </div>
                    <span className="text-xs font-bold text-[#2A324B]/60 uppercase tracking-wider">Son:mi assessment</span>
                  </div>
                  {bullets ? (
                    <ul className="space-y-1.5">
                      {bullets.map((b, i) => (
                        <li key={i} className="flex gap-2 text-sm text-[#2A324B]/80 leading-relaxed">
                          <span className="text-[#C8956A] flex-shrink-0 mt-0.5">•</span>
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-[#2A324B]/80 leading-relaxed">{aiSummary}</p>
                  )}
                  <p className="text-xs text-[#2A324B]/40">This is Son:mi's read — adjust the sliders below if anything looks off to you.</p>
                </div>
              );
            })()}
          </div>

          {/* ── Routine prompt ─────────────────────────────────────────── */}
          {!hasRoutine && onUploadRoutine && (
            <div className="bg-gradient-to-br from-[#FDF6EE] to-[#F8E8D4] border border-[#F3D5B5]/50 rounded-2xl p-4 flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-[#C8956A]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#2A324B] text-sm">Add your routine for sharper reads</p>
                <p className="text-xs text-[#2A324B]/55 mt-0.5">Snap your shelf — we'll match the analysis to what you're actually using.</p>
                <button
                  type="button"
                  onClick={onUploadRoutine}
                  className="mt-2 text-xs font-semibold text-[#C8956A] hover:text-[#A87A52] flex items-center gap-1"
                >
                  Upload routine
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* ── Overall rating ─────────────────────────────────────────── */}
          <div>
            <h3 className="font-semibold text-[#2A324B] text-sm mb-3">Overall</h3>
            <Slider
              value={overallScore}
              onChange={setOverallScore}
              min={1}
              max={10}
              minLabel="Poor (1)"
              maxLabel="Excellent (10)"
            />
          </div>

          {/* ── Concerns ───────────────────────────────────────────────── */}
          <div>
            <h3 className="font-semibold text-[#2A324B] text-sm mb-4">Concerns</h3>
            <div className="space-y-5">
              {METRICS.map(({ key, label }) => (
                <Slider
                  key={key}
                  label={label}
                  value={metrics[key]}
                  onChange={val => setMetrics(prev => ({ ...prev, [key]: val }))}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Save button */}
        <div className="px-5 pb-8 pt-3 flex-shrink-0 border-t border-[#F0F0F0]">
          {error && <p className="text-red-500 text-xs text-center mb-3">{error}</p>}
          <button
            onClick={handleSave}
            disabled={saving || scanLoading}
            className="w-full bg-[#2A324B] text-[#F3D5B5] py-3.5 rounded-2xl font-semibold text-sm disabled:opacity-50 transition-opacity"
          >
            {saving ? 'Saving…' : isEditing ? 'Save Changes' : 'Save Entry'}
          </button>
        </div>
      </div>
    </div>
  );
}
